import React, { useEffect, useMemo, useState } from 'react';
import { Text, useTooltip } from '@weave/design-system';
import { Button } from '@weave/platform-ui-components';
import { TableStyles } from '../../../styles/table-style';
import { theme } from '@weave/theme-original';
import { css } from '@emotion/core';
import ReactTable from 'react-table';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { DataCenterMoverContainer } from './data-center-mover/data-center-mover.container';
import { Page } from '../../shared';
import { prettifyTimestamp } from '../../../helpers/time';
import {
  requestRebootDevices,
  requestListDevices,
  requestDisableAllDevices,
  requestEnableAllDevices,
  selectDevicesUpdating,
  selectDevicesArray,
  requestDeleteSoftphone,
} from '../../../redux/actions/devices';
import { selectCurrentLocationId } from '../../../redux/actions/location';
import { Store } from '../../../redux/store/store.model';
import { selectHasWeaveAcl } from '../../../redux/actions/auth/auth.selectors';
import { ConfirmationModal } from '../../shared/confirmation-modal/confirmation-modal.component';
import PhoneWarningImg from '../../../images/phone-warning.png';
import { CoreACLs } from '../../../redux/actions/auth/auth.types';
import { DevicesQueries } from '../../../apis/devices/v2/queries';
import {
  ListDevice,
  DeviceInclude_Enum,
  DeviceMake_Enum,
  DeviceType_Enum,
  ProvisionHealth_Enum,
  ProvisionAuthentication_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { useAlert } from '@weave/alert-system';
import { formatDate } from '../../../helpers/date';
import { DevicesMutations } from '../../../apis/devices/v2/mutations';
import { useQueryClient } from 'react-query';
import { devicesQueryKeys } from '../../../apis/devices/v2/keys';
import { selectLocationChildren } from 'redux/actions/location/location-search';

export const Devices = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const alert = useAlert();
  const [sipStatusModalOpen, setSipStatusModalOpen] = useState<boolean>(false);
  const [openDestroySoftphoneModal, setOpenDestroySoftphoneModal] =
    useState<boolean>(false);
  const [deviceID, setDeviceId] = useState<string>('');
  const { canEditSipProfiles, locationId } = useSelector((store: Store) => ({
    canEditSipProfiles: selectHasWeaveAcl(store, CoreACLs.SIPSTATUSWRITE),
    locationId: selectCurrentLocationId(store),
  }));
  const childrenLocations = useSelector(selectLocationChildren);
  const childrenLocationIds = childrenLocations.map((location) => location.LocationID);

  const queryClient = useQueryClient();

  const { data: devicesV2 = [], refetch: refetchDevicesList } =
    DevicesQueries.useGetDevicesList(
      {
        locationIds: [locationId, ...childrenLocationIds],
        include: [DeviceInclude_Enum.REGISTRATION, DeviceInclude_Enum.PROVISION],
      },
      {
        select: (data) => data.devices ?? [],
        onError: () => {
          alert.error('Error fetching devices v2 provisioning data.');
        },
      }
    );

  const { mutate: unlockProvisioning, isLoading: isUnlocking } =
    DevicesMutations.useUnlockProvisioningMutation();

  // Map devices by ID for easy access mapping to the device rows (which use v1 data). It would
  // require a big migration effort to fully transition to v2 so for now we keep this mapping
  // to get the latest v2 data (which includes the device provisioning data).
  const devicesV2MapById = useMemo(() => {
    return devicesV2.reduce((acc, device) => {
      acc[device.deviceId] = device;
      return acc;
    }, {} as Record<string, typeof devicesV2[number]>);
  }, [devicesV2]);

  useEffect(() => {
    dispatch(requestListDevices());
  }, [locationId]);
  const { loading: loadingDevices, devices } = useSelector(selectDevicesArray);
  const { loading: updating } = useSelector(selectDevicesUpdating);

  const filteredDevices = devices.filter(
    (device) => device.type === 'desk_phone' || device.type === 'softphone'
  );
  const devicesEnabled = filteredDevices.some((device) => !device.sipProfile?.disabled);
  const deviceIds = filteredDevices.map((device) => device.ID);

  const confirmedSoftphone = useMemo(() => {
    return filteredDevices.find((el) => el.ID === deviceID);
  }, [deviceID, filteredDevices]);

  const handleRefresh = () => {
    refetchDevicesList();
    dispatch(requestListDevices());
  };

  const handleUnlockAllDevices = () => {
    const devicesToUnlock = devicesV2.filter(
      (device) =>
        device.type === DeviceType_Enum.DESK_PHONE &&
        device.make === DeviceMake_Enum.YEALINK
    );
    const phoneIds = devicesToUnlock.map((device) => device.deviceId);

    unlockProvisioning(
      { deviceIds: phoneIds },
      {
        onSuccess: (resp) => {
          const count = resp.unlockedDevices.length;
          alert.success(`Successfully unlocked provisioning for ${count} phones.`);
          handleRefresh();
        },
        onError: () => {
          alert.error('Error unlocking provisioning for all phones.');
          handleRefresh();
        },
      }
    );
  };

  const handleUnlockDevice = (deviceId: string) => {
    const deviceV2 = devicesV2MapById[deviceId];

    unlockProvisioning(
      { deviceIds: [deviceId] },
      {
        onSuccess: () => {
          alert.success(`Successfully unlocked provisioning for ${deviceV2.name}.`);
          queryClient.invalidateQueries([...devicesQueryKeys.base, 'devices']);
        },
        onError: () => {
          alert.error('Error unlocking provisioning for device.');
          handleRefresh();
        },
      }
    );
  };

  return (
    <Page
      title="Devices"
      loading={loadingDevices || updating}
      headerActions={
        <div
          css={css`
            display: inline-block;
          `}
        >
          <DataCenterMoverContainer />
          <Button
            onClick={handleUnlockAllDevices}
            disabled={isUnlocking}
            css={css`
              display: inline-block !important;
              margin: 0 8px;
            `}
          >
            Unlock All
          </Button>
          <Button
            onClick={() => dispatch(requestRebootDevices(deviceIds))}
            css={css`
              display: inline-block !important;
              margin: 0 8px;
            `}
          >
            Resync All
          </Button>
          <Button
            css={css`
              display: inline-block !important;
            `}
            disabled={loadingDevices}
            onClick={handleRefresh}
          >
            Refresh Data
          </Button>
        </div>
      }
    >
      <ConfirmationModal
        title={`Are you sure you want to ${
          devicesEnabled ? 'disable' : 'enable'
        } all devices for this location?`}
        isOpen={sipStatusModalOpen}
        onRequestClose={() => setSipStatusModalOpen(false)}
        onConfirmClick={() =>
          dispatch(
            devicesEnabled ? requestDisableAllDevices() : requestEnableAllDevices()
          )
        }
        confirmBtnTitle={`${devicesEnabled ? 'Disable' : 'Enable'} Devices`}
        image={
          <img
            src={PhoneWarningImg}
            css={css`
              height: 100px;
              margin-bottom: 32px;
            `}
            alt="Warning"
          />
        }
        note={
          devicesEnabled
            ? 'Only disable if fraud has been detected. Doing this will disable all SIPs, kill all current calls, and turn off outbound calls, except for 911 and Weave Support. It will automatically resync the phone, updating the line keys to call Weave.'
            : 'Enabling will turn on all SIPs and allow outbound calls again. Only do this if all fraud threats have been addressed.'
        }
      />
      <ConfirmationModal
        isOpen={openDestroySoftphoneModal}
        onRequestClose={() => setOpenDestroySoftphoneModal(false)}
        onConfirmClick={() => {
          dispatch(requestDeleteSoftphone(confirmedSoftphone?.ID ?? ''));
          setDeviceId('');
          setOpenDestroySoftphoneModal(false);
        }}
        confirmBtnColor="red"
        confirmBtnTitle="Destroy"
        title={`Are you sure you want to destroy ${
          confirmedSoftphone?.name ?? 'this softphone'
        }?`}
      />
      <Text size="medium">Total: {filteredDevices.length}</Text>
      <ReactTable
        columns={[
          {
            Header: 'Name',
            id: 'name',
            accessor: (device) => device.sipProfile?.name ?? '-',
            minWidth: 250,
            style: overflowStyles,
          },
          {
            Header: 'Agent',
            id: 'agent',
            accessor: (device) =>
              device.type === 'softphone'
                ? `Weave Softphone ${
                    !!device.registration ? device.registration?.user_agent : ''
                  }`
                : device.registration?.user_agent ?? '-',
            minWidth: 275,
            style: overflowStyles,
          },
          {
            Header: 'Region',
            id: 'region',
            accessor: (device) => device.registration?.region ?? '-',
            minWidth: 110,
            style: overflowStyles,
          },
          {
            Header: 'External IP',
            id: 'externalIP',
            accessor: (device) => device.registration?.addr ?? '-',
            minWidth: 330,
            style: overflowStyles,
          },
          {
            Header: 'Last Connected',
            id: 'lastConnected',
            minWidth: 200,
            style: overflowStyles,
            accessor: (device) =>
              prettifyTimestamp(device.registration?.modified ?? '') || '-',
          },
          {
            Header: 'Provisioning',
            id: 'provisioning',
            width: 125,
            style: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            accessor: (device) => {
              const deviceV2 = devicesV2MapById[device.ID];

              if (!deviceV2) return '-';

              return <ProvisionStatus device={deviceV2} />;
            },
          },
          {
            Header: 'Registered',
            id: 'registered',
            width: 125,
            style: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            accessor: (device) => {
              return (
                <span
                  css={css`
                    border-radius: 50%;
                    height: 8px;
                    width: 8px;
                    background-color: ${device.isRegistered
                      ? theme.colors.success
                      : theme.colors.error};
                  `}
                />
              );
            },
          },
          {
            Header: 'SIP Enabled',
            id: 'sipEnabled',
            width: 125,
            style: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            },
            accessor: (device) => {
              return (
                <span
                  css={css`
                    border-radius: 50%;
                    height: 8px;
                    width: 8px;
                    background-color: ${device.sipProfile?.disabled
                      ? theme.colors.error
                      : theme.colors.success};
                  `}
                />
              );
            },
          },
          {
            Header: 'Actions',
            id: 'actions',
            width: 340,
            accessor: (device) => {
              const deviceV2 = devicesV2MapById[device.ID];
              const provision = deviceV2?.provision;

              const showUnlockButton =
                provision?.authentication === ProvisionAuthentication_Enum.UNLOCKED ||
                provision?.authentication === ProvisionAuthentication_Enum.LOCKED;

              return (
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                  `}
                >
                  {device.type === 'softphone' && (
                    <Button
                      color="red"
                      size="small"
                      onClick={() => {
                        setDeviceId(device.ID);
                        setOpenDestroySoftphoneModal(true);
                      }}
                    >
                      Destroy
                    </Button>
                  )}

                  {showUnlockButton && (
                    <Button
                      fill="outline"
                      size="small"
                      onClick={() => {
                        handleUnlockDevice(device.ID);
                      }}
                    >
                      Unlock Provisioning
                    </Button>
                  )}

                  {!!device.isRegistered && (
                    <Button
                      fill="outline"
                      size="small"
                      css={css`
                        display: inline-block !important;
                        width: auto;
                      `}
                      onClick={() => dispatch(requestRebootDevices([device.ID]))}
                    >
                      Resync Device
                    </Button>
                  )}
                </div>
              );
            },
          },
        ]}
        data={filteredDevices}
        showPagination={false}
        loading={false}
        pageSize={filteredDevices.length || 5}
        defaultSorted={[{ id: 'name', desc: false }]}
        css={[
          TableStyles,
          css`
            margin: 24px 0;

            .rt-th,
            .rt-td {
              padding: 12px 16px !important;
            }

            .rt-th {
              .rt-resizable-header-content {
                padding-left: 0 !important;
              }
            }

            .rt-tr-group {
              cursor: pointer;

              :hover {
                background: ${theme.colors.gray100};
              }
            }
          `,
        ]}
        getTdProps={(state, row, column) => {
          if (row?.original?.ID) {
            return {
              onClick: (e) => {
                if (column.id === 'actions') {
                  // Don't redirect on action button clicks
                  return;
                }

                // Redirect to the device page on row clicks
                history.push(
                  `/location/${locationId}/phones/devices/${row?.original?.ID}`
                );
              },
            };
          }

          return {};
        }}
      />
    </Page>
  );
};

const overflowStyles = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'auto',
};

const ProvisionStatus = ({ device }: { device: ListDevice }) => {
  const provision = device.provision;

  const { triggerProps, Tooltip, tooltipProps } = useTooltip();

  if (!provision) return <span>-</span>;

  return (
    <div
      {...triggerProps}
      css={css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      `}
    >
      <span
        css={css`
          border-radius: 50%;
          height: 8px;
          width: 8px;
          background-color: ${provision.health === ProvisionHealth_Enum.PROVISIONING
            ? theme.colors.success
            : theme.colors.error};
        `}
      />
      <Tooltip {...tooltipProps}>
        <div
          css={css`
            width: max-content;
            max-width: 224px;
          `}
        >
          <Text size="medium" color="white">
            {!!provision.provisionedAt
              ? `Last Provisioned on ${formatDate(
                  provision.provisionedAt,
                  'MMM D, YYYY; hh:mm A z'
                )}`
              : 'Unknown provisioned date'}
          </Text>

          {provision.authentication !== ProvisionAuthentication_Enum.DISABLED && (
            <Text size="medium" color="white">
              {provision.authentication === ProvisionAuthentication_Enum.LOCKED
                ? 'Secured provisioning'
                : provision.authentication === ProvisionAuthentication_Enum.UNLOCKED
                ? `Unlocked provisioning until ${formatDate(
                    provision.authenticationEnforcedAt,
                    'MMM D, YYYY; hh:mm A z'
                  )}`
                : 'Authentication status unknown.'}
            </Text>
          )}
        </div>
      </Tooltip>
    </div>
  );
};
